import { PublicFooter } from "@/components/public-footer"
import { PublicHeader } from "@/components/public-header"
import { SEO } from "@/components/seo"

export default function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy - StitchPDF"
        description="Learn how StitchPDF protects your data. Your files are processed in-browser and never uploaded to our servers. Read our full privacy commitment."
        canonical="https://stitchpdf.com/privacy"
        ogDescription="Learn how StitchPDF protects your data and privacy."
      />

      <div className="bg-background flex min-h-screen flex-col">
        <PublicHeader />

        <main className="container mx-auto flex-1 px-4 py-16">
          <div className="prose dark:prose-invert mx-auto max-w-3xl">
            <h1>Privacy Policy</h1>
            <p className="lead">Last updated: February 28, 2026</p>

            <h2>1. Who We Are</h2>
            <p>
              StitchPDF is operated by Gorg Rails Tech Solutions, Limitada, a
              company established in Portugal.
            </p>
            <p>
              For the purposes of data protection laws, we act as the data
              controller of your personal data.
            </p>
            <p>For privacy inquiries, contact: support@stitchpdf.com</p>

            <h2>2. Overview</h2>
            <p>StitchPDF is designed to minimize data collection.</p>
            <p>
              Most PDF operations (merge, split, reorder, sign, image
              conversion) are processed locally in your browser using your
              device&apos;s resources.
            </p>
            <p>
              Files are only transmitted to our servers when necessary, such as
              for password protection or when you are signed in and choose to
              store files in your dashboard.
            </p>
            <p>Our servers are located in Germany (European Union).</p>

            <h2>3. Data We Collect</h2>
            <p>We may collect the following categories of data:</p>
            <h3>Account Information</h3>
            <p>When you create an account, we collect:</p>
            <ul>
              <li>Email address</li>
              <li>Encrypted password</li>
            </ul>
            <h3>Payment Information</h3>
            <p>
              Payments are processed by Stripe. We do not store credit card
              details. Stripe processes payment data according to its own{" "}
              <a
                href="https://stripe.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                privacy policy
              </a>
              .
            </p>
            <h3>Usage Data</h3>
            <p>
              We use Google Analytics to understand how our service is used
              (e.g., pages visited, features used).
            </p>
            <p>
              You can opt out using your browser settings or the{" "}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Analytics opt-out extension
              </a>
              .
            </p>

            <h2>4. Your Files</h2>
            <h3>Processing</h3>
            <p>Most PDF operations are processed entirely in your browser.</p>
            <p>
              Password protection requires temporary server-side processing.
              Files used for password protection are automatically deleted
              within 24 hours.
            </p>
            <h3>Storage</h3>
            <p>
              If you are signed in, processed files may be stored on our servers
              so they are accessible from your dashboard.
            </p>
            <p>
              For free accounts, dashboard files are automatically deleted after
              24 hours. For paid accounts (Plus and Pro), files remain stored
              until you delete them.
            </p>
            <p>Files are encrypted at rest.</p>
            <p>
              When you delete a file, it is permanently removed from our
              systems.
            </p>
            <p>
              Anonymous users&apos; files are not stored on our servers, except
              temporary processing required for password protection.
            </p>
            <h3>Saved Signatures</h3>
            <p>Saved signatures are stored securely until you delete them.</p>

            <h2>5. Legal Basis for Processing (EEA Users)</h2>
            <p>
              Under the General Data Protection Regulation (GDPR), we rely on
              the following legal bases:
            </p>
            <ul>
              <li>
                <strong>Performance of a contract</strong> — to provide PDF
                services and manage your account
              </li>
              <li>
                <strong>Legitimate interests</strong> — to ensure security and
                prevent abuse
              </li>
              <li>
                <strong>Consent</strong> — for analytics cookies
              </li>
            </ul>

            <h2>6. Data Retention</h2>
            <ul>
              <li>
                Account data is retained while your account remains active.
              </li>
              <li>
                Free account files are automatically deleted after 24 hours.
              </li>
              <li>
                Paid account files (Plus and Pro) remain until deleted by you.
              </li>
              <li>
                Password-protection processing files are deleted within 24
                hours.
              </li>
              <li>
                Analytics data retention is governed by Google Analytics
                settings.
              </li>
            </ul>

            <h2>7. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to
              protect your data, including:
            </p>
            <ul>
              <li>HTTPS encryption (TLS)</li>
              <li>Secure password hashing</li>
              <li>Encrypted storage</li>
              <li>Restricted server access</li>
            </ul>

            <h2>8. International Data Transfers</h2>
            <p>
              All primary data processing occurs within the European Union
              (Germany).
            </p>
            <p>
              If any service providers process data outside the EU, we ensure
              appropriate safeguards are in place as required by law.
            </p>

            <h2>9. Your Rights</h2>
            <p>Under GDPR, you have the right to:</p>
            <ul>
              <li>Access your personal data</li>
              <li>Request correction</li>
              <li>Request deletion</li>
              <li>Restrict or object to processing</li>
              <li>Request data portability</li>
              <li>Lodge a complaint with your local supervisory authority</li>
            </ul>
            <p>You can delete your files directly from your dashboard.</p>
            <p>
              To delete your account and associated data, contact{" "}
              support@stitchpdf.com.
            </p>

            <h2>10. Children</h2>
            <p>StitchPDF is not intended for users under the age of 13.</p>

            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Continued use
              of the service after changes constitutes acceptance of the updated
              policy.
            </p>
          </div>
        </main>

        <PublicFooter />
      </div>
    </>
  )
}
